import { ReactNode, memo } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "../../hooks";
import { usePermissions, UserRole } from "../../hooks/usePermissions";
import { CircularProgress, Box } from "@mui/material";

/**
 * Enhanced protected route component with performance optimizations
 *
 * Features:
 * - Memoized to prevent unnecessary re-renders
 * - Role-based access control
 * - Clean loading state
 * - Preserves navigation state
 */

interface ProtectedRouteProps {
  children: ReactNode;
  requiredRoles?: string[];
}

const ProtectedRoute = ({
  children,
  requiredRoles = [],
}: ProtectedRouteProps) => {
  const { isAuthenticated, isLoading, isInitialized, user } = useAuth();
  const { hasAccess } = usePermissions();
  const location = useLocation();

  // Show loading indicator while auth is initializing
  if (isLoading || !isInitialized) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          bgcolor: "background.default",
        }}
      >
        <CircularProgress size={40} />
      </Box>
    );
  }

  // Check if user is authenticated
  if (!isAuthenticated) {
    // Redirect to login page, but save the location they were trying to access
    return <Navigate to="/auth/login" state={{ from: location }} replace />;
  }

  // Check if user needs to reset password
  if (
    user?.forcePasswordChange &&
    location.pathname !== "/auth/forced-password-reset"
  ) {
    // User must reset password, redirect to forced password reset page
    return <Navigate to="/auth/forced-password-reset" replace />;
  }

  // Check for required roles if specified
  if (requiredRoles.length > 0) {
    // Fix: Properly type the requiredRoles for hasAccess function
    const hasRequiredRole = hasAccess(requiredRoles as UserRole[]);

    if (!hasRequiredRole) {
      // User doesn't have required role, redirect to unauthorized page
      return <Navigate to="/unauthorized" replace />;
    }
  }

  // User is authenticated and has required role, render children
  return <>{children}</>;
};

// Memoize the component to prevent unnecessary re-renders
// This is particularly important for route components that could re-render often
export default memo(ProtectedRoute);
